<!--
  * SharePointPreviewerModal.vue
  * 
  * A modal component that combines row editing capabilities with SharePoint file preview.
  * Features a two-panel layout with EditSectionsContent.vue on the left and document preview on the right.
  * 
-->
<template>
  <b-modal
    id="file-preview-modal"
    size="xl"
    visible
    hide-footer
    body-class="p-0"
    dialog-class="custom__dialog"
    @hidden="$emit('hidden')"
  >
    <!-- Custom header to separate Edit Row from Document Preview -->
    <template #modal-header>
      <div class="d-flex w-100 align-items-center p-0 m-0 position-relative">
        <div class="header-left">
          <h5 class="m-0">
            Edit Row
          </h5>
        </div>
        
        <div class="header-right d-flex justify-content-start">
          <div class="document-controls d-flex align-items-center">
            <b-button
              v-if="documents.length"
              id="open-sharepoint-btn"
              variant="primary"
              :href="selectedDocument?.webUrl"
              target="_blank"
              class="text-nowrap"
            >
              <font-awesome-icon
                :icon="['fas', 'external-link-alt']"
                class="mr-1"
              />
              Open in SharePoint
            </b-button>
            <!-- Dropdown to switch documents -->
            <b-form-select
              v-if="documents?.length > 1"
              id="document-select"
              class="ml-3"
              :options="documentOptions"
              :value="selectedDocument ? selectedDocument.id || selectedDocument.name : ''"
              @change="onDocumentChange"
            />
          </div>
        </div>
        
        <b-button-close
          class="ml-auto"
          @click="$bvModal.hide('file-preview-modal')"
        />
      </div>
      <!-- Divider between the two sections of modal -->
      <div class="modal-divider" />
    </template>

    <div class="two-panel-container d-flex">
      <!-- Left Panel: Edit Sections -->
      <div class="left-panel">
        <div class="panel-content">
          <EditSectionsContent
            ref="editContent"
            :is-embedded="true"
            :is-file-previewer="true"
            @changed-row="onChangedRow"
          />
        </div>
        
        <!-- Panel Footer -->
        <div class="left-panel-footer">
          <b-button
            id="cancel-btn"
            variant="secondary"
            class="mr-2"
            @click="onCancel"
          >
            Cancel
          </b-button>
          <b-button
            id="ok-btn"
            variant="primary"
            @click="onSave"
          >
            OK
          </b-button>
        </div>
      </div>      

      <!-- Right Panel: Document Preview -->
      <div class="right-panel">
        <div class="panel-content">
          <div
            v-if="loading"
            class="text-center p-4"
          >
            <b-spinner variant="primary" />
            <p class="mt-2">
              Loading document preview...
            </p>
          </div>
          
          <show-error 
            v-if="error" 
            id="error"
            :show="!!error" 
            :text="error"  
          />
          
          <div
            v-else
            class="preview-frame"
          >
            <template v-if="documents?.length === 0 && !loading && !error">
              <div class="no-preview d-flex flex-column justify-content-center align-items-center text-center p-4">
                <font-awesome-icon
                  :icon="['fas', 'file-alt']"
                  size="3x"
                  class="text-muted mb-3"
                />
                <p>No documents available to preview</p>
              </div>
            </template>

            <template v-else>
              <iframe
                v-if="previewUrl"
                :src="previewUrl"
                width="100%"
                height="100%"
                frameborder="0"
              />
              <div
                v-else-if="!loading && !error"
                class="no-preview d-flex flex-column justify-content-center align-items-center text-center p-4"
              >
                <font-awesome-icon
                  :icon="['fas', 'file-alt']"
                  size="3x"
                  class="text-muted mb-3"
                />
                <p>Preview not available for this file</p>
                <b-button
                  variant="primary"
                  :href="selectedDocument?.webUrl"
                  target="_blank"
                >
                  <font-awesome-icon
                    :icon="['fas', 'external-link-alt']"
                    class="mr-2"
                  />
                  Open in SharePoint
                </b-button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import { getFileDownloadUrl, getBlobUrl } from '@/core/utils/sharePointUtils';
// Components
import EditSectionsContent from '@/core/components/complex-table/EditSectionsContent.vue';
import showError from '@/core/components/ShowError.vue';

const {
  mapState: designerMapState,
} = createNamespacedHelpers('designer');

export default {
  name: 'SharePointPreviewerModal',
  components: {
    EditSectionsContent,
    showError,
  },
  props: {
    documents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewUrl: null,
      selectedDocument: null,
    };
  },
  watch: {
    // Watch for changes in documents prop to reset selected document
    documents(newDocs) {
      if (newDocs && newDocs.length > 0) {
        this.selectedDocument = newDocs[0];
        this.loadDocumentPreview();
      } else {
        this.selectedDocument = null;
        this.previewUrl = null;
      }
    },
  },
  computed: {
    ...designerMapState({
      modalData: state => state.data,
    }),
    // Transform documents into dropdown options
    documentOptions() {
      if (!this.documents || this.documents.length === 0) {
        return [];
      }
      
      return this.documents.map(doc => ({
        value: doc.id || doc.name,
        text: doc.name,
        doc: doc, // Store reference to original document object
      }));
    },
  },
  methods: {
    // Update selected document and load preview
    selectDocument(document) {
      this.selectedDocument = document;
      this.loadDocumentPreview();
    },
    // Handle document dropdown change
    onDocumentChange(value) {
      const option = this.documentOptions.find(opt => opt.value === value);
      if (option && option.doc) {
        this.selectDocument(option.doc);
      }
    },
    // Save changes via EditSectionsContent
    async onSave() {
      if (this.$refs.editContent) {
        const result = await this.$refs.editContent.save();
        if (result?.success) {
          // Modal will close automatically via the shared content component
        }
      }
    },
    // Cancel editing and close modal
    onCancel() {
      if (this.$refs.editContent) {
        this.$refs.editContent.cancel();
      }
      this.$bvModal.hide('file-preview-modal');
    },
    // Forward changed-row event to parent
    onChangedRow(params) {
      this.$emit('changed-row', params);
    },
    // Load document preview based on file type
    async loadDocumentPreview() {
      if (!this.selectedDocument) return;

      this.loading = true;
      this.error = null;
      this.previewUrl = null;

      try {
        // Try to fetch file metadata to check permissions before embedding
        const downloadUrl = await getFileDownloadUrl(this.selectedDocument.driveId, this.selectedDocument.id);
        // If has permission, check if it supports embedding
        if (downloadUrl && this.isEmbedSupported(this.selectedDocument.name)) {
          this.previewUrl = this.constructEmbedUrl(this.selectedDocument.webUrl);
        // If embedding is not supported, generate blob URL for preview
        } else if (downloadUrl) {
          this.previewUrl = await this.generatePreviewUrl(downloadUrl);
        }
      } catch (err) {
        if (err?.response?.status === 403 || /access denied/i.test(err?.message)) {
          this.error = 'You do not have permission to view this document.';
        } else {
          this.error = 'Failed to load document preview';
        }
      } finally {
        this.loading = false;
      }
      
    },
    // Check if file type supports embed preview (office files)
    isEmbedSupported(fileName) {
      const supportedTypes = ['docx', 'xlsx', 'pptx'];
      const extension = fileName.split('.').pop().toLowerCase();
      return supportedTypes.includes(extension);
    },
    // Construct SharePoint embed URL for preview (office files)
    constructEmbedUrl(webUrl) {
      if (!webUrl) return '';
      // Remove everything after the file extension
      const match = webUrl.match(/^(.*\.(docx|xlsx|pptx))/i);
      const baseUrl = match ? match[1] : webUrl;
      webUrl = baseUrl;
      return `${webUrl}&action=embedview`;
    },
    // Generate blob URL for non-office file types
    async generatePreviewUrl(downloadUrl) {
      try {
        if (downloadUrl) return await getBlobUrl(downloadUrl);
      } catch (err) {
        throw err;
      }
    },
  },
  mounted() {
    // Initialise preview with first document if available
    if (this.documents && this.documents.length > 0) {
      this.selectedDocument = this.documents[0];
      
      if (this.selectedDocument) {
        this.loadDocumentPreview();
      }
    }
  },
  created() {
    // Apply dynamic modal sizing from designer state
    if (this.modalData?.ModalHeight && this.modalData?.ModalWidth) {
      document.body.style.setProperty('--height', this.modalData.ModalHeight);
      document.body.style.setProperty('--width', this.modalData.ModalWidth);
    }
  },
};
</script>

<style lang="scss">
.custom__dialog {
  width: var(--width) !important;
  max-width: var(--width) !important;
  min-width: var(--width) !important;
  height: var(--height) !important;
  min-height: var(--height) !important;
}
</style>

<style scoped>
/* Custom header styling */
.header-left {
  flex: 1;
  padding-right: 1rem;
}

.header-left h5 {
  color: #495057;
}

.header-right {
  flex: 1;
  padding-left: 1rem;
}

.document-controls {
  padding: 0 1rem;
}

.modal-divider {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #dee2e6;
  transform: translateX(-50%);
}

/* Two-panel layout */
.two-panel-container {
  height: calc(100vh - 200px);
  gap: 0;
}

.left-panel,
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 300px;
}

.right-panel {
  background-color: #e9ecef;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

/* Left panel footer styling - matches Bootstrap modal footer */
.left-panel-footer {
  border-top: 1px solid #dee2e6;
  background-color: #fff;
  padding: 0.75rem 1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  border-bottom-left-radius: calc(0.3rem - 1px);
}

/* Preview frame styling */
.preview-frame {
  height: 100%;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.no-preview {
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  min-height: 300px;
}
</style>